import { FormEvent, useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daio283p14a1h3mr77v0";
const MAX_MESSAGE = 500;

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      setMessage("");
      form.reset();
      return;
    }

    if (message.length > MAX_MESSAGE) {
      setStatus("error");
      setFormError(`留言不能超过 ${MAX_MESSAGE} 字，请精简一下。`);
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      const text = typeof value === "string" ? value.trim() : "";
      if (text) payload.append(key, text);
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setMessage("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查连接后重试。");
    }
  };

  return (
    <form
      data-readdy-form
      id="contact-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
            怎么称呼你
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="昵称即可"
            className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
            联系邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <label htmlFor="contact-topic" className="block text-sm text-foreground-700 mt-4 mb-1.5">
        留言类型
      </label>
      <select
        id="contact-topic"
        name="topic"
        className="w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
      >
        <option value="话题推荐">话题推荐</option>
        <option value="内容纠错">内容纠错</option>
        <option value="合作洽谈">合作洽谈</option>
        <option value="其他建议">其他建议</option>
      </select>

      <label htmlFor="contact-message" className="block text-sm text-foreground-700 mt-4 mb-1.5">
        留言内容
      </label>
      <textarea
        id="contact-message"
        name="message"
        required
        rows={5}
        maxLength={MAX_MESSAGE}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="想推荐的话题、发现的问题，或者合作想法，都可以写在这里。"
        className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none"
      ></textarea>
      <p className="mt-1 text-right text-xs text-foreground-400">
        {message.length} / {MAX_MESSAGE}
      </p>

      <input
        className="form-guard-field"
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <button
        type="submit"
        disabled={status === "loading"}
        className="mt-4 inline-flex items-center justify-center gap-1.5 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
      >
        <i className="ri-send-plane-line"></i>
        {status === "loading" ? "正在提交…" : "发送留言"}
      </button>

      {status === "success" && (
        <p className="mt-3 text-sm text-secondary-700 flex items-center gap-1.5">
          <i className="ri-checkbox-circle-fill"></i>
          留言已收到，我们会认真阅读，必要时通过邮箱回复你。
        </p>
      )}
      {status === "error" && (
        <p className="mt-3 text-sm text-primary-700 flex items-center gap-1.5">
          <i className="ri-error-warning-fill"></i>
          {formError}
        </p>
      )}
    </form>
  );
}